import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, QrCode, AlertTriangle, Lock } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { supabase } from '@/integrations/supabase/client';

/**
 * QR Redirect page handles universal QR codes and redirects to the appropriate product page
 * URL format: /qr/product/{productId}?company={companyId}
 */
const QRRedirect = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { companyId: currentCompanyId } = useAuth();
  
  const qrCompanyId = searchParams.get('company');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  useEffect(() => {
    const validateAndRedirect = async () => {
      if (!productId) {
        navigate('/products');
        return;
      }

      if (!currentCompanyId) {
        setError('Kullanıcı oturumu bulunamadı');
        setLoading(false);
        return;
      }

      try {
        // If QR code doesn't have company ID, deny access
        if (!qrCompanyId) {
          setError('Geçersiz QR kod formatı');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Check if user's company matches QR code's company
        if (qrCompanyId !== currentCompanyId) {
          setError('Bu QR kod farklı bir şirkete aittir');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Verify that the product exists and belongs to the company
        const { data: product, error: productError } = await supabase
          .from('products')
          .select('id, company_id, name')
          .eq('id', productId)
          .eq('company_id', currentCompanyId)
          .single();

        if (productError || !product) {
          setError('Ürün bulunamadı veya erişim yetkiniz yok');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // All checks passed, redirect to product
        navigate(`/products/${productId}`);
        
      } catch (err) {
        console.error('QR validation error:', err);
        setError('QR kod doğrulanırken hata oluştu');
        setLoading(false);
      }
    };

    // Add a small delay to show loading state
    const timer = setTimeout(validateAndRedirect, 1000);
    
    return () => clearTimeout(timer);
  }, [productId, qrCompanyId, currentCompanyId, navigate]);

  if (error) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="w-full max-w-md">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mb-6">
                {accessDenied ? (
                  <Lock className="w-8 h-8 text-destructive" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                )}
              </div>
              
              <h2 className="text-xl font-semibold mb-2 text-destructive">
                {accessDenied ? 'Erişim Engellendi' : 'Hata Oluştu'}
              </h2>
              <p className="text-muted-foreground text-sm mb-6">
                {error}
              </p>
              
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/products')}
                >
                  Ürünlere Git
                </Button>
                <Button 
                  onClick={() => navigate('/qr-scan')}
                >
                  Tekrar Tara
                </Button>
              </div>
              
              {productId && (
                <p className="text-xs text-muted-foreground mt-4 font-mono">
                  ID: {productId}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mb-6">
              <QrCode className="w-8 h-8 text-accent" />
            </div>
            
            {loading && (
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mb-4" />
            )}
            
            <h2 className="text-xl font-semibold mb-2">QR Kod İşleniyor</h2>
            <p className="text-muted-foreground text-sm">
              Güvenlik kontrolleri yapılıyor...
            </p>
            
            {productId && (
              <p className="text-xs text-muted-foreground mt-4 font-mono">
                ID: {productId}
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default QRRedirect;
